import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import ApiService from '../../services/api';
import { persistAdminPortalSession } from '../../utils/adminPortalSession';
import { clearClientAuthSession } from '../../utils/clearClientAuthSession';
import { hasClientAuthSession, getClientAccessToken } from '../../utils/sessionAuth';

interface ExpertRouteProps {
  children: React.ReactNode;
}

const ExpertRoute: React.FC<ExpertRouteProps> = ({ children }) => {
  const [checking, setChecking] = useState(true);
  const [isExpert, setIsExpert] = useState(false);

  useEffect(() => {
    const checkExpertStatus = async () => {
      if (!hasClientAuthSession()) {
        setIsExpert(false);
        setChecking(false);
        return;
      }

      try {
        const response = await ApiService.request('/auth/me');
        const user = response?.data?.user;
        const hasExpertRole = user?.role === 'expert';
        const token = getClientAccessToken();

        if (hasExpertRole && user) {
          persistAdminPortalSession(user, token);
        }

        setIsExpert(hasExpertRole);

        if (!hasExpertRole) {
          localStorage.removeItem('adminUser');
        }
      } catch (error: unknown) {
        console.error('Expert session validation failed:', error);
        const status = (error as { response?: { status?: number }; status?: number })?.response?.status
          ?? (error as { status?: number })?.status;
        if (status === 401 || !hasClientAuthSession()) {
          clearClientAuthSession();
        }
        setIsExpert(false);
      } finally {
        setChecking(false);
      }
    };

    checkExpertStatus();
  }, []);

  if (checking) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!isExpert) {
    return <Navigate to={hasClientAuthSession() ? '/dashboard' : '/login'} replace />;
  }

  return <>{children}</>;
};

export default ExpertRoute;
